import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { cloneDeep } from "lodash";
import {
  Button,
  ColumnLayout,
  createListSelector,
  Form,
  FormField,
  Modal,
  ModalBody,
  ModalHeader,
  SimpleDropdown,
  translate
} from "clavis-nxt-components-custdevtm";
import { ModuleEntity } from "../../domain/ModuleEntity";
import { TranslateForAdminEntity } from "../../domain/TranslateForAdminEntity";
import { TranslationValueEntity } from "../../domain/TranslationValueEntity";
import "./TranslateModal.scss";

class TranslateModalComponent extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      entity: this.initializeEntity(),
      loading: false
    };
  }

  /** Generate translate.
   *  @param {Object} formProps - Form object. */
  generate = formProps => {
    if (this.props.modify) {
      formProps
        .update(this.state.entity)
        .promise.then(response => this.generateCallback(response))
        .catch(() => this.setLoading());
    } else {
      formProps
        .insert(this.state.entity)
        .promise.then(response => this.generateCallback(response))
        .catch(() => this.setLoading());
    }
  };

  /** Generate function callback.
   *  @param {Object} response - Generate action response object. */
  generateCallback = response =>
    response.status === 200 ? this.props.toggle() : this.setLoading();

  /** Initialize working entity.
   *  @return {TranslateForAdminEntity} */
  initializeEntity = () => {
    if (this.props.modify) {
      return this.props.entity;
    } else {
      const entity = new TranslateForAdminEntity();
      entity.translationValue = new TranslationValueEntity();
      entity.moduleCode = this.props.moduleCode;
      return entity;
    }
  };

  /** Select module code.
   *  @param {ModuleEntity} moduleCode - Instance of ModuleEntity. */
  onSelectedModuleCode = moduleCode => {
    const entity = cloneDeep(this.state.entity);
    entity.moduleCode = moduleCode.code;
    this.setEntity(entity);
  };

  /** Check required fields.
   *  @param {number|string} value - Field value.
   *  @return {?string[]}*/
  required = value => (!value && value !== 0 ? ["*Required"] : undefined);

  /** Form set entity function.
   *  @param {TranslateForAdminEntity} entity - Instance of TranslateForAdminEntity. */
  setEntity = entity => this.setState({ entity });

  /** Toggling loading variable.
   *  @param {function} [callback] - Callback function. Run after set loading. */
  setLoading = callback => {
    this.setState(
      prevState => ({
        loading: !prevState.loading
      }),
      () => {
        if (callback && typeof callback === "function") {
          callback();
        }
      }
    );
  };

  render() {
    const { t } = this.props;

    return (
      <div>
        <Modal
          className={"new-translate-data"}
          isOpen={this.props.isOpen}
          size={"lg"}
        >
          <ModalHeader toggle={this.props.toggle}>
            <div className="huge-font-medium header-color">
              <div className="exit" onClick={this.props.toggle} />
              <div>{t("page.TranslationPage.newModal.Title")}</div>
            </div>
          </ModalHeader>
          <ModalBody>
            <Form
              entity={this.state.entity}
              onChange={this.setEntity}
              render={formProps => (
                <div className="new-instrument-second-step-modal-container">
                  <ColumnLayout width={6}>
                    <FormField
                      disabled={this.props.modify || this.state.loading}
                      property="labelCode"
                      title={t(
                        "page.TranslationPage.TranslateForAdminEntity.labelCode"
                      )}
                      validate={this.required}
                    />
                    <SimpleDropdown
                      data={this.props.modules}
                      disabled={this.props.modify || this.state.loading}
                      label={t(
                        "page.TranslationPage.TranslateForAdminEntity.moduleCode"
                      )}
                      labelProperty={"code"}
                      onSelected={this.onSelectedModuleCode}
                      selected={this.props.modules.find(
                        module => module.code === this.state.entity.moduleCode
                      )}
                      noTranslate
                      sortByAsc
                    />
                    <FormField
                      disabled={this.state.loading}
                      property="translationValue.en"
                      title={t(
                        "page.TranslationPage.TranslateForAdminEntity.translationValue.en"
                      )}
                    />
                    <FormField
                      disabled={this.state.loading}
                      property="translationValue.hu"
                      title={t(
                        "page.TranslationPage.TranslateForAdminEntity.translationValue.hu"
                      )}
                    />
                  </ColumnLayout>
                  <div className={"button-container"}>
                    <Button
                      buttonType="secondary"
                      disabled={this.state.loading}
                      onClick={this.props.toggle}
                    >
                      {t("page.TranslationPage.newModal.cancel")}
                    </Button>
                    <Button
                      buttonType="primary"
                      disabled={!formProps.valid && formProps.touchedForm}
                      loading={this.state.loading}
                      onClick={() => {
                        formProps.setTouchedForm();
                        if (formProps.valid) {
                          this.setLoading(() => this.generate(formProps));
                        }
                      }}
                    >
                      {t("page.TranslationPage.newModal.save")}
                    </Button>
                  </div>
                </div>
              )}
            />
          </ModalBody>
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  return {
    modules: createListSelector(ModuleEntity, "ModuleEntity")(state, props)
  };
};

export const TranslateModal = translate()(
  connect(
    mapStateToProps,
    null
  )(TranslateModalComponent)
);

TranslateModal.propTypes = {
  /** Instance of TranslateForAdminEntity. */
  entity: PropTypes.instanceOf(TranslateForAdminEntity),
  /** Boolean to control the state of modal. */
  isOpen: PropTypes.bool,
  /** Modify indicator. */
  modify: PropTypes.bool,
  /** Selected module code. */
  moduleCode: PropTypes.string.isRequired,
  /** Callback for toggling modal isOpen variable. */
  toggle: PropTypes.func.isRequired
};

TranslateModal.defaultProps = {
  isOpen: false,
  modify: false
};
